import type { DecisionEngineChannelType } from "@hightouch/lib/customer-data/decision-engine/types";
import type { DecisionEngineDestinationDefinition } from "./definition";

const passthrough = (column: string) => ({
  type: "standard" as const,
  from: column,
  to: column,
});

// Mirrors the surface/messaging SELECT in resolver-queries.ts, minus
// hightouch_user, whose live re-join churned every row on every sync.
function buildMappings(
  channelType: DecisionEngineChannelType,
  maxSlots: number,
) {
  const isSurface = channelType === "surface";
  const mappings = [
    passthrough("external_id"),
    passthrough("id"),
    passthrough("campaign_id"),
    passthrough("send_at"),
    passthrough("hightouch_recommendation"),
  ];

  if (isSurface) {
    mappings.push(passthrough("experiment"));
  }

  if (isSurface && maxSlots > 1) {
    for (let slot = 0; slot < maxSlots; slot++) {
      mappings.push(passthrough(`message_id_${slot}`));
      mappings.push(passthrough(`hightouch_recommendation_${slot}`));
    }
  } else {
    mappings.push(passthrough("message_id"));
  }

  return mappings;
}

export const mongodbDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "mongodb",
  category: "batch",
  // Dumps the AID row as-is, including its interaction columns.
  producers: ["aid"],

  generateSyncConfig: ({ scopeId, channelType, settings }) => ({
    mode: "upsert",
    object: settings.collectionName || `aid-${scopeId}`,
    externalIdMapping: {
      from: "external_id",
      to: settings.mongoIdField || "_id",
    },
    autoSyncColumns: false,
    mappings: buildMappings(channelType, settings.placement?.max_slots ?? 1),
  }),

  syncStrategy: {
    supportsChannelLevelSync: true,
    shouldManageSyncs: true,
    getSyncSchedule: () => ({
      type: "cron",
      schedule: { expression: "0 * * * *" },
    }),
  },
  // MongoDB is the one AID destination whose batch-size key is `batchSize`
  // (not `rowsPerBatch`); 10k mirrors the rETL form max. No mode knob — the
  // sync only supports upsert, which AID already emits.
  syncTuning: {
    batchSize: {
      configKey: "batchSize",
      max: 10_000,
      label: "Documents per bulk write",
      description: "Documents processed together for each MongoDB bulk write.",
    },
  },
};
